(() => {
  if (window.KoppaCarousels) return;
  const labels = JSON.parse(document.getElementById('KoppaCarouselLabels')?.textContent || '{}');
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const rails = new Map();
  const stateful = new Map();
  let serial = 0;
  const dirty = new Set();
  let flushing = 0;
  function requestUpdate(rail) {
    dirty.add(rail);
    if (flushing) return;
    flushing = requestAnimationFrame(() => {
      flushing = 0;
      const batch = [...dirty];
      dirty.clear();
      const states = batch.map(item => item.measure());
      batch.forEach((item, index) => item.apply(states[index]));
    });
  }
  const visible = element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
  const rtl = element => getComputedStyle(element).direction === 'rtl';
  const smooth = () => motion.matches ? 'instant' : 'smooth';
  const on = (element, type, callback, signal, options = {}) => element.addEventListener(type, callback, {...options, signal});
  const arrow = direction => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${direction < 0 ? 'm14 6-6 6 6 6M8 12h13' : 'm10 6 6 6-6 6M16 12H3'}"/></svg>`;
  function gesture(element, move, signal, edge = null) {
    let start = null;
    on(element, 'touchstart', event => {
      start = event.touches.length === 1 ? {x:event.touches[0].clientX,y:event.touches[0].clientY,edge:edge?.()} : null;
    }, signal, {passive:true});
    on(element, 'touchend', event => {
      if (!start || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - start.x;
      const dy = event.changedTouches[0].clientY - start.y;
      const direction = (dx < 0 ? 1 : -1) * (rtl(element) ? -1 : 1);
      const allow = !edge || (direction > 0 ? start.edge.end : start.edge.start);
      start = null;
      if (allow && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) move(direction);
    }, signal, {passive:true});
    on(element, 'touchcancel', () => { start = null; }, signal, {passive:true});
  }
  function nativeRail(viewport, options = {}) {
    if (rails.has(viewport)) return;
    const items = options.items || [...viewport.children];
    if (items.length < 2) return;
    const abort = new AbortController(), signal = abort.signal;
    const host = options.host || viewport;
    let controls = options.controls;
    let generated = false;
    if (!controls) {
      generated = true;
      controls = document.createElement('div');
      controls.className = 'koppa-rail-controls';
      const previous = document.createElement('button');
      previous.type = 'button'; previous.dataset.loopPrevious = ''; previous.ariaLabel = labels.previous || 'Previous'; previous.innerHTML = arrow(-1);
      const next = document.createElement('button');
      next.type = 'button'; next.dataset.loopNext = ''; next.ariaLabel = labels.next || 'Next'; next.innerHTML = arrow(1);
      const position = document.createElement('span'); position.className = 'koppa-rail-position'; position.setAttribute('aria-live', 'polite'); position.setAttribute('aria-atomic', 'true');
      controls.append(previous, position, next);
      viewport.after(controls);
    }
    const previous = options.previous || controls.querySelector('[data-loop-previous], [data-previous], [data-protocol-review-prev], button[name="previous"]');
    const next = options.next || controls.querySelector('[data-loop-next], [data-next], [data-protocol-review-next], button[name="next"]');
    const position = controls.querySelector('.koppa-rail-position, .kg-position, [data-protocol-review-position]');
    if (!previous || !next) { if (generated) controls.remove(); return; }
    if (!viewport.id) viewport.id = 'KoppaRail-' + (++serial);
    previous.setAttribute('aria-controls', viewport.id); next.setAttribute('aria-controls', viewport.id);
    const oldTabindex = viewport.getAttribute('tabindex');
    const offset = () => Math.abs(viewport.scrollLeft);
    const maximum = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const startEdge = () => (parseFloat(getComputedStyle(viewport).paddingInlineStart) || 0) + 3;
    const positions = () => {
      const frame = viewport.getBoundingClientRect();
      const style = getComputedStyle(viewport);
      const padding = parseFloat(rtl(viewport) ? style.scrollPaddingRight : style.scrollPaddingLeft) || 0;
      return [...new Set(items.map((item,index) => {
        if (index === 0) return 0;
        const box = item.getBoundingClientRect();
        const distance = rtl(viewport) ? frame.right - box.right : box.left - frame.left;
        return Math.min(maximum(), Math.max(0, Math.round(offset() + distance - viewport.clientLeft - padding)));
      }))];
    };
    let active = false, frame = 0;
    // Split into a read phase and a write phase so that several rails can be
    // measured together and then updated together (see requestUpdate below).
    // Interleaving them forced a synchronous layout per rail on page load
    // (PageSpeed 2026-09-30: 178 ms on mobile).
    const measure = () => {
      const isActive = visible(viewport) && maximum() > 3;
      let text = null;
      if (position && isActive) {
        const box = viewport.getBoundingClientRect();
        const shown = items.map((item, index) => ({index,rect:item.getBoundingClientRect()})).filter(({rect}) => Math.min(rect.right,box.right)-Math.max(rect.left,box.left)>Math.min(rect.width*.45,40));
        const first = shown[0]?.index ?? 0, last = shown.at(-1)?.index ?? first;
        text = `${first+1}${last > first ? '–'+(last+1) : ''} / ${items.length}`;
      }
      return {isActive, text};
    };
    const apply = ({isActive, text}) => {
      active = isActive;
      if (generated || options.toggleControls) controls.hidden = !active;
      viewport.setAttribute('tabindex', active ? '0' : (oldTabindex || '-1'));
      previous.disabled = !active; next.disabled = !active;
      previous.setAttribute('aria-disabled', String(!active)); next.setAttribute('aria-disabled', String(!active));
      if (text !== null && position.textContent !== text) position.textContent = text;
    };
    const update = () => apply(measure());
    const rail = {measure, apply};
    const go = value => viewport.scrollTo({left:Math.max(0,Math.min(maximum(),value))*(rtl(viewport)?-1:1),behavior:smooth()});
    const move = direction => {
      if (!active) return;
      const value = offset(), points = positions();
      const first = points[0], last = points.at(-1);
      if (direction > 0) go(value >= last-3 ? 0 : (points.find(point => point > value+3) ?? maximum()));
      else go(value <= startEdge() ? last : (points.filter(point => point < value-3).at(-1) ?? 0));
    };
    for (const [button,direction] of [[previous,-1],[next,1]]) on(button,'click',event => { event.preventDefault(); event.stopImmediatePropagation(); move(direction); },signal,{capture:true});
    on(viewport,'keydown',event => {
      if (event.target !== viewport || !active) return;
      if (['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (event.key === 'Home' || event.key === 'End') go(event.key === 'Home' ? 0 : maximum());
        else move((event.key === 'ArrowRight' ? 1 : -1)*(rtl(viewport)?-1:1));
      }
    },signal,{capture:true});
    gesture(viewport,move,signal,()=>{const points=positions();return {start:offset()<=startEdge(),end:offset()>=points.at(-1)-3};});
    on(viewport,'scroll',()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(update);},signal,{passive:true});
    const resize = new ResizeObserver(() => requestUpdate(rail));resize.observe(viewport);
    items.forEach(item=>resize.observe(item));
    viewport.dataset.koppaRail = 'true';
    if (!viewport.hasAttribute('aria-label')) viewport.setAttribute('aria-label', viewport.closest('section')?.querySelector('h2,[aria-level="2"]')?.textContent.trim() || labels.carousel || 'Carousel');
    viewport.setAttribute('aria-roledescription', labels.carousel || 'Carousel');
    if (!viewport.hasAttribute('role')) viewport.setAttribute('role','region');
    if (host.matches('koppa-gallery')) host.dataset.ready = 'true';
    if (host.matches('slider-component,slideshow-component')) {host.enableSliderLooping = true;host.pause?.();}
    // The footer ribbon is the intentional autoplay exception to manual site carousels.
    let autoplay = null;
    if (host.matches('koppa-gallery[data-mode="ribbon"]')) {
      host.dataset.autoplay = 'true';
      let hovered = false;
      on(host,'pointerenter',event=>{if(event.pointerType === 'mouse') hovered=true;},signal);
      on(host,'pointerleave',()=>{hovered=false;},signal);
      autoplay = setInterval(() => {
        const box = viewport.getBoundingClientRect();
        if (!host.isConnected || document.hidden || motion.matches || hovered || host.contains(document.activeElement) || box.bottom <= 0 || box.top >= innerHeight) return;
        move(1);
      }, 1700);
    }
    rails.set(viewport,{move,update,destroy(){clearInterval(autoplay);abort.abort();resize.disconnect();cancelAnimationFrame(frame);dirty.delete(rail);if(generated)controls.remove();rails.delete(viewport);}});
    requestUpdate(rail);
  }
  function embla(viewport) {
    if (stateful.has(viewport) || !viewport.koppaCarouselApi) return;
    const api = viewport.koppaCarouselApi, abort = new AbortController(), signal = abort.signal;
    const count = Number(viewport.dataset.koppaCount) || api.slideNodes().length;
    // Keep Embla's offset measurements local despite the theme's position reset.
    viewport.style.setProperty('position','relative','important');
    api.containerNode().style.setProperty('position','relative','important');
    viewport.tabIndex = 0;viewport.setAttribute('role','region');viewport.setAttribute('aria-roledescription',labels.carousel || 'Carousel');
    viewport.setAttribute('aria-label',viewport.closest('section')?.querySelector('h2,[aria-level="2"]')?.textContent.trim() || labels.carousel || 'Carousel');
    const update = () => {
      const inView = api.slidesInView();
      api.slideNodes().forEach((slide,index)=>{
        const show = inView.includes(index);
        slide.setAttribute('aria-hidden',String(!show));slide.inert = !show;
        slide.setAttribute('aria-label',`${index%count+1} / ${count}`);
      });
      viewport.dataset.koppaIndex=String(api.selectedScrollSnap()%count);
    };
    const keyboard = event => {
      if(event.target!==viewport)return;
      if(['ArrowLeft','ArrowRight','Home','End'].includes(event.key)){
        event.preventDefault();
        if(event.key==='Home'||event.key==='End')api.scrollTo(event.key==='Home'?0:count-1,motion.matches);
        else (event.key==='ArrowRight' !== rtl(viewport) ? api.scrollNext : api.scrollPrev)(motion.matches);
      }
    };
    on(viewport,'keydown',keyboard,signal);
    api.on('select',update);api.on('slidesInView',update);api.on('reInit',update);
    api.reInit({loop:count>1,direction:rtl(viewport)?'rtl':'ltr',duration:motion.matches?0:25});
    update();
    stateful.set(viewport,{destroy(){abort.abort();api.off('select',update);api.off('slidesInView',update);api.off('reInit',update);stateful.delete(viewport);}});
  }
  function review(root) {
    if(stateful.has(root))return;
    const previous=()=>root.querySelector('button[aria-label^="Previous"],button[aria-label*="السابق"]');
    const next=()=>root.querySelector('button[aria-label^="Next"],button[aria-label*="التالي"]');
    if(!previous()||!next())return;
    const abort=new AbortController();
    const move=direction=>(direction>0?next():previous())?.click();
    gesture(root,move,abort.signal);
    root.tabIndex=0;
    on(root,'keydown',event=>{if(event.target!==root)return;if(event.key==='ArrowLeft'||event.key==='ArrowRight'){event.preventDefault();move((event.key==='ArrowRight'?1:-1)*(rtl(root)?-1:1));}},abort.signal);
    stateful.set(root,{destroy(){abort.abort();stateful.delete(root);}});
  }
  function images(root) {
    if (stateful.has(root)) return;
    const slides = [...root.querySelectorAll('.cs-cslide')];
    if (slides.length < 2) return;
    const abort = new AbortController(), signal = abort.signal;
    const controls = document.createElement('div');
    controls.className = 'koppa-rail-controls';
    const previous = document.createElement('button'), next = document.createElement('button'), position = document.createElement('span');
    for (const [button,direction] of [[previous,-1],[next,1]]) {
      button.type = 'button';button.ariaLabel = direction < 0 ? labels.previous : labels.next;button.innerHTML = arrow(direction);
      on(button,'click',()=>go(current+direction),signal);
    }
    position.className = 'koppa-rail-position';position.setAttribute('aria-live','polite');
    controls.append(previous,position,next);root.after(controls);
    root.querySelector('.cs-carousel__dots')?.setAttribute('hidden','');
    let current = 0;
    const go = index => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide,i)=>{slide.classList.toggle('is-active',i===current);slide.setAttribute('aria-hidden',String(i!==current));});
      position.textContent = `${current+1} / ${slides.length}`;root.dataset.koppaIndex = current;
    };
    root.tabIndex = 0;root.dataset.koppaImages = 'true';root.setAttribute('role','region');root.setAttribute('aria-label',slides[0].alt || labels.carousel);root.setAttribute('aria-roledescription',labels.carousel);
    gesture(root,direction=>go(current+direction),signal);
    on(root,'keydown',event=>{if(event.target!==root)return;if(event.key==='ArrowLeft'||event.key==='ArrowRight'){event.preventDefault();go(current+(event.key==='ArrowRight'?1:-1)*(rtl(root)?-1:1));}},signal);
    go(0);
    stateful.set(root,{destroy(){abort.abort();controls.remove();stateful.delete(root);}});
  }
  function scan() {
    document.querySelectorAll('[data-protocol-review-track]').forEach(viewport=>nativeRail(viewport,{controls:viewport.closest('#product-reviews').querySelector('[data-protocol-review-controls]'),toggleControls:true}));
    document.querySelectorAll('koppa-gallery').forEach(host=>nativeRail(host.querySelector('.kg-viewport'),{host,items:[...host.querySelector('.kg-track').children],controls:host.querySelector('.kg-controls'),toggleControls:true}));
    document.querySelectorAll('slider-component,slideshow-component').forEach(host=>{
      const viewport=host.querySelector('[id^="Slider-"]'),controls=host.querySelector('.slider-buttons');
      if(viewport&&controls)nativeRail(viewport,{host,controls,previous:host.querySelector('button[name="previous"]'),next:host.querySelector('button[name="next"]')});
    });
    document.querySelectorAll('main .snap-x').forEach(viewport=>{
      if(viewport.closest('[data-ploy-product-gallery]')||viewport.matches('[data-protocol-review-track]')||viewport.closest('slider-component,slideshow-component'))return;
      nativeRail(viewport);
    });
    document.querySelectorAll('.cs-carousel').forEach(images);
    document.querySelectorAll('[data-koppa-embla]').forEach(embla);
    document.querySelectorAll('.product-reviews,.product-hero__customer-quote').forEach(review);
    for(const [element,controller] of [...rails,...stateful])if(!element.isConnected)controller.destroy();
  }
  let scheduled = false;
  const schedule = () => {if(scheduled)return;scheduled=true;requestAnimationFrame(()=>{scheduled=false;scan();});};
  const observer = new MutationObserver(records => {if(records.some(record=>record.type==='attributes'||[...record.addedNodes].some(node=>node.nodeType===1)))schedule();});
  observer.observe(document.body,{childList:true,subtree:true,attributes:true,attributeFilter:['data-koppa-embla']});
  document.addEventListener('shopify:section:load',schedule);
  document.addEventListener('shopify:section:unload',event=>{for(const [element,controller]of [...rails,...stateful])if(event.target.contains(element))controller.destroy();});
  motion.addEventListener('change',()=>{stateful.forEach((value,element)=>element.koppaCarouselApi?.reInit({duration:motion.matches?0:25}));});
  window.KoppaCarousels={scan,rails};
  scan();
})();
